import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { User, Lock, CreditCard, Save, Eye, EyeOff, Wallet } from 'lucide-react';
import axios from 'axios';

const UserProfile = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [banks, setBanks] = useState({});
  
  // Profile form
  const [profileForm, setProfileForm] = useState({
    voornaam: '',
    van: '',
    geboortedatum: '',
    telefoon: '',
    adres: ''
  });
  
  // Password form
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  // Bank form
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    branch_code: '',
    account_number: '',
    account_holder_name: '',
    account_type: 'savings'
  });

  useEffect(() => {
    fetchProfile();
    fetchBanks();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/my/profile`);
      setProfile(response.data);
      
      // Populate profile form
      setProfileForm({
        voornaam: response.data.voornaam || '',
        van: response.data.van || '',
        geboortedatum: response.data.geboortedatum || '',
        telefoon: response.data.telefoon || '',
        adres: response.data.adres || ''
      });
      
      // Populate bank form if exists
      if (response.data.bank_account) {
        setBankForm({
          bank_name: response.data.bank_account.bank_name || '',
          branch_code: response.data.bank_account.branch_code || '',
          account_number: response.data.bank_account.account_number || '',
          account_holder_name: response.data.bank_account.account_holder_name || '',
          account_type: response.data.bank_account.account_type || 'savings'
        });
      }
    } catch (error) {
      toast.error('Kon nie profiel laai nie');
    } finally {
      setLoading(false);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${API}/banks`);
      setBanks(response.data);
    } catch (error) {
      console.error('Could not fetch banks');
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const formData = new FormData();
      Object.entries(profileForm).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      
      await axios.put(`${API}/my/profile`, formData);
      toast.success('Profiel opgedateer');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie profiel opdateer nie');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Nuwe wagwoorde stem nie ooreen nie');
      return;
    }
    
    if (passwordForm.new_password.length < 8) {
      toast.error('Wagwoord moet ten minste 8 karakters wees');
      return;
    }
    
    setSaving(true);
    
    try {
      const formData = new FormData();
      formData.append('current_password', passwordForm.current_password);
      formData.append('new_password', passwordForm.new_password);
      
      await axios.post(`${API}/my/change-password`, formData);
      toast.success('Wagwoord suksesvol verander');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie wagwoord verander nie');
    } finally {
      setSaving(false);
    }
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const formData = new FormData();
      Object.entries(bankForm).forEach(([key, value]) => {
        formData.append(key, value);
      });
      
      await axios.post(`${API}/my/bank-account`, formData);
      toast.success('Bankbesonderhede opgedateer');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie bankbesonderhede opdateer nie');
    } finally {
      setSaving(false);
    }
  };

  const handleBankSelect = (bankName) => {
    setBankForm({
      ...bankForm,
      bank_name: bankName,
      branch_code: banks[bankName] || ''
    });
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrateur',
      handler: 'Hanteerder',
      writer: 'Skrywer',
      proofreader: 'Proefleeser'
    };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-2xl font-bold">
          {profile?.voornaam?.charAt(0) || profile?.email?.charAt(0) || '?'}
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {profile?.voornaam} {profile?.van}
          </h1>
          <p className="text-gray-600">{profile?.email}</p>
          <span className="inline-block px-2 py-1 mt-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
            {getRoleLabel(profile?.role)}
          </span>
        </div>
      </div>

      {/* Balance Card (if user has balance) */}
      {profile?.balance && (
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Wallet className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm text-green-600">Jou Balans</p>
                <p className="text-2xl font-bold text-green-700">
                  R{(profile.balance.current_balance || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Profiel
          </TabsTrigger>
          <TabsTrigger value="password" className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Wagwoord
          </TabsTrigger>
          <TabsTrigger value="bank" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Bankbesonderhede
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>Persoonlike Inligting</CardTitle>
              <CardDescription>Dateer jou persoonlike besonderhede op</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="voornaam">Voornaam</Label>
                    <Input
                      id="voornaam"
                      value={profileForm.voornaam}
                      onChange={(e) => setProfileForm({...profileForm, voornaam: e.target.value})}
                      placeholder="Jou voornaam"
                      data-testid="profile-voornaam"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="van">Van</Label>
                    <Input
                      id="van"
                      value={profileForm.van}
                      onChange={(e) => setProfileForm({...profileForm, van: e.target.value})}
                      placeholder="Jou van"
                      data-testid="profile-van"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="geboortedatum">Geboortedatum</Label>
                    <Input
                      id="geboortedatum"
                      type="date"
                      value={profileForm.geboortedatum}
                      onChange={(e) => setProfileForm({...profileForm, geboortedatum: e.target.value})}
                      data-testid="profile-geboortedatum"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telefoon">Telefoonnommer</Label>
                    <Input
                      id="telefoon"
                      type="tel"
                      value={profileForm.telefoon}
                      onChange={(e) => setProfileForm({...profileForm, telefoon: e.target.value})}
                      placeholder="bv. 0821234567"
                      data-testid="profile-telefoon"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="adres">Adres</Label>
                  <Input
                    id="adres"
                    value={profileForm.adres}
                    onChange={(e) => setProfileForm({...profileForm, adres: e.target.value})}
                    placeholder="Jou adres"
                    data-testid="profile-adres"
                  />
                </div>
                
                {/* Read-only fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label>E-pos (kan nie verander nie)</Label>
                    <Input value={profile?.email || ''} disabled className="bg-gray-50" />
                  </div>
                  <div className="space-y-2">
                    <Label>Rol</Label>
                    <Input value={getRoleLabel(profile?.role)} disabled className="bg-gray-50" />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                  data-testid="save-profile-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Stoor...' : 'Stoor Veranderinge'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Password Tab */}
        <TabsContent value="password">
          <Card>
            <CardHeader>
              <CardTitle>Verander Wagwoord</CardTitle>
              <CardDescription>Kies 'n sterk wagwoord met ten minste 8 karakters</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current_password">Huidige Wagwoord</Label>
                  <div className="relative">
                    <Input
                      id="current_password"
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                      placeholder="Voer huidige wagwoord in"
                      data-testid="current-password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                    >
                      {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="new_password">Nuwe Wagwoord</Label>
                  <div className="relative">
                    <Input
                      id="new_password"
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                      placeholder="Voer nuwe wagwoord in"
                      data-testid="new-password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                    >
                      {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Bevestig Nuwe Wagwoord</Label>
                  <div className="relative">
                    <Input
                      id="confirm_password"
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                      placeholder="Bevestig nuwe wagwoord"
                      data-testid="confirm-password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                    >
                      {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={saving || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                  data-testid="change-password-btn"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  {saving ? 'Verander...' : 'Verander Wagwoord'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Details Tab */}
        <TabsContent value="bank">
          <Card>
            <CardHeader>
              <CardTitle>Bankbesonderhede</CardTitle>
              <CardDescription>Voeg jou bankbesonderhede by vir betalings</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBankSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank</Label>
                  <Select value={bankForm.bank_name} onValueChange={handleBankSelect}>
                    <SelectTrigger data-testid="bank-select">
                      <SelectValue placeholder="Kies jou bank" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(banks).map((bank) => (
                        <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="branch_code">Takkode</Label>
                    <Input
                      id="branch_code"
                      value={bankForm.branch_code}
                      onChange={(e) => setBankForm({...bankForm, branch_code: e.target.value})}
                      placeholder="bv. 632005"
                      data-testid="branch-code"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="account_type">Rekening Tipe</Label>
                    <Select 
                      value={bankForm.account_type} 
                      onValueChange={(v) => setBankForm({...bankForm, account_type: v})}
                    >
                      <SelectTrigger data-testid="account-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="savings">Spaarrekening</SelectItem>
                        <SelectItem value="cheque">Tjekrekening</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="account_number">Rekeningnommer</Label>
                  <Input
                    id="account_number"
                    value={bankForm.account_number}
                    onChange={(e) => setBankForm({...bankForm, account_number: e.target.value})}
                    placeholder="Jou rekeningnommer"
                    data-testid="account-number"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="account_holder_name">Rekeninghouer Naam</Label>
                  <Input
                    id="account_holder_name"
                    value={bankForm.account_holder_name}
                    onChange={(e) => setBankForm({...bankForm, account_holder_name: e.target.value})}
                    placeholder="Naam soos op bankrekening"
                    data-testid="account-holder-name"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={saving || !bankForm.bank_name || !bankForm.account_number || !bankForm.account_holder_name}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                  data-testid="save-bank-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Stoor...' : 'Stoor Bankbesonderhede'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UserProfile;
